import { Box, Typography, styled } from '@mui/material';
import { EmptyCartIcon } from '../icons';
import { CurrencyField, TextField, VerticalSpace } from '@chargeafter/ui';
import { CartTotal } from './CartTotal';
import {
  orderFormDetails,
  SELF_FINANCING_PRODUCT,
  cartTotalErrorMessage,
  ButtonName,
} from '../constants';
import { ItemsList } from './ItemsList';
import { useEffect, useState } from 'react';
import {
  calcCart,
  useSingleLenderCartAmountError,
} from '../services/cartManager';
import {
  getValueFromCartDetails,
  useAdvancedCartDetails,
} from '../services/cartManager';
import { saveToUserSession } from '../services/userSessionStorageManager';
import {
  useGetApprovedAmount,
  useGetFinancialProductType,
} from '../services/flowManager';
import { NumberFormatValues } from 'react-number-format';
import { isNumberAllowed } from '../formsUtils';
import { TrackerButton } from './TrackerButtonWrapper';
import { useSetCurrentModal } from '../services/modalManager';
import { CartDetails } from '@chargeafter/payment-sdk';

const StyledEmptyCartIcon = styled(EmptyCartIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  margin: 'auto',
  width: theme.spacing(34),
  height: theme.spacing(26),
}));

const EmptyCartContainer = styled(Box)(() => ({
  textAlign: 'center',
}));

const StyledButton = styled(TrackerButton)(({ theme }) => ({
  width: theme.spacing(24),
  borderRadius: theme.spacing(1),
}));

const CartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: `calc(100% - ${theme.spacing(10)})`,
  alignItems: 'center',
  flexDirection: 'column',
  display: 'flex',
  justifyContent: 'center',
}));

const BarDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  justifyContent: 'space-between',
  padding: theme.spacing(4, 0),
}));

const StyledCartEmptyText = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

const OrderAdditionalDetailsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 4, 4, 4),
  textAlign: 'center',
}));
interface OrderDetailsErrors {
  taxAmount?: string;
  merchantOrderId?: string;
  shippingAmount?: string;
  discounts?: string;
}

export const AdvancedCart = () => {
  const [advancedCartDetails, setAdvancedCartDetails] =
    useAdvancedCartDetails();
  const financialProductType = useGetFinancialProductType();

  const selfFinancing =
    financialProductType && financialProductType === SELF_FINANCING_PRODUCT;
  const approvedAmount = useGetApprovedAmount();

  const hasApprovedAmount = approvedAmount > 0;
  const maxAmount =
    hasApprovedAmount && !selfFinancing ? approvedAmount : 999999.99;
  const [errors, setErrors] = useState<OrderDetailsErrors>();

  const updateCurrentModal = useSetCurrentModal();

  const singleLenderCartAmountError = useSingleLenderCartAmountError();

  useEffect(() => {
    const currentAdvancedCartDetails = { ...advancedCartDetails };

    currentAdvancedCartDetails.totalAmount = calcCart(
      currentAdvancedCartDetails,
    );
    setAdvancedCartDetails(currentAdvancedCartDetails);
    handleErrors(currentAdvancedCartDetails);
  }, [advancedCartDetails.items]);

  const handleErrors = (currentAdvancedCartDetails: CartDetails) => {
    const newErrors: OrderDetailsErrors = {};
    const currentTotalAmount = calcCart(currentAdvancedCartDetails);
    newErrors.discounts =
      currentTotalAmount < 0
        ? orderFormDetails.discount.validations.amountExceeding?.errorMessage
        : '';
    newErrors.shippingAmount =
      !!currentAdvancedCartDetails.shippingAmount &&
      currentTotalAmount > maxAmount
        ? orderFormDetails.shipping.validations.amountExceeding?.errorMessage
        : '';
    newErrors.taxAmount =
      currentAdvancedCartDetails.taxAmount && currentTotalAmount > maxAmount
        ? orderFormDetails.tax.validations.amountExceeding?.errorMessage
        : '';

    if (currentAdvancedCartDetails?.merchantOrderId)
      newErrors.merchantOrderId =
        (currentAdvancedCartDetails?.merchantOrderId?.length || 0) > 24
          ? orderFormDetails.orderId.validations.length?.errorMessage
          : '';

    if (
      currentAdvancedCartDetails?.merchantOrderId &&
      !orderFormDetails.orderId.validations.invalidCharacters?.regex?.test(
        currentAdvancedCartDetails?.merchantOrderId,
      )
    ) {
      newErrors.merchantOrderId =
        orderFormDetails.orderId.validations.invalidCharacters?.errorMessage;
    }

    setErrors({ ...newErrors });
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const currentAdvancedCartDetails = { ...advancedCartDetails };

    if (name === orderFormDetails.discount.name) {
      currentAdvancedCartDetails.discounts = [
        {
          amount: +value,
          name: 'Discount',
        },
      ];
    } else if (name === orderFormDetails.orderId.name) {
      currentAdvancedCartDetails.merchantOrderId = value;
    } else {
      currentAdvancedCartDetails[name] = +value;
    }
    handleErrors(currentAdvancedCartDetails);
    setAdvancedCartDetails(currentAdvancedCartDetails);
    saveToUserSession('advancedCart', currentAdvancedCartDetails);
  };

  const addItem = () => {
    updateCurrentModal('cartItem');
  };

  const cartTotalQuantity = advancedCartDetails.items.reduce((acc, item) => {
    return acc + +item.quantity;
  }, 0);

  const totalAmount = calcCart(advancedCartDetails);

  const discountAmount = advancedCartDetails.discounts
    ?.map((discount) => discount.amount)
    .reduce((acc, amount) => acc + amount, 0);
  const isAllowedDiscount = (values: NumberFormatValues) =>
    +values.value <=
      totalAmount + (advancedCartDetails?.discounts?.[0].amount || 0) ||
    (totalAmount < 0 &&
      +values.value <= (advancedCartDetails?.discounts?.[0].amount || 0));

  return (
    <CartContainer>
      {advancedCartDetails.items.length < 1 ? (
        <EmptyCartContainer>
          <StyledEmptyCartIcon />
          <StyledCartEmptyText variant="h5">
            Your cart is empty
          </StyledCartEmptyText>
        </EmptyCartContainer>
      ) : (
        <ItemsList />
      )}

      <VerticalSpace units={1} />
      <StyledButton
        disabled={maxAmount <= totalAmount}
        buttonName={ButtonName.OpenNewItemAdvanceCart}
        data-testid="add-item-button"
        onClick={addItem}
        icon="add"
      >
        Add Item
      </StyledButton>
      <VerticalSpace units={2} />
      {advancedCartDetails.items.length > 0 && (
        <OrderAdditionalDetailsContainer>
          <BarDetails>
            <TextField
              onChange={handleValueChange}
              label="Order ID"
              value={getValueFromCartDetails(
                advancedCartDetails.merchantOrderId,
              ).toString()}
              name={orderFormDetails.orderId.name}
              error={!!errors?.[orderFormDetails.orderId.name]}
              helperText={errors?.merchantOrderId}
              inputProps={{
                'data-testId': 'merchant-order-id',
                maxLength:
                  orderFormDetails.orderId.validations.length?.maxLength,
              }}
            />
            <CurrencyField
              label="Tax"
              value={getValueFromCartDetails(advancedCartDetails.taxAmount)}
              onChange={handleValueChange}
              name={orderFormDetails.tax.name}
              error={!!errors?.[orderFormDetails.tax.name]}
              helperText={errors?.taxAmount}
              isAllowed={isNumberAllowed}
              data-testid="tax-amount"
              decimalScale={2}
            />
            <CurrencyField
              label="Shipping"
              value={getValueFromCartDetails(
                advancedCartDetails.shippingAmount,
              )}
              onChange={handleValueChange}
              error={!!errors?.[orderFormDetails.shipping.name]}
              helperText={errors?.shippingAmount}
              name={orderFormDetails.shipping.name}
              isAllowed={isNumberAllowed}
              data-testid="shipping-amount"
              decimalScale={2}
            />
            <CurrencyField
              label="Discount"
              decimalScale={2}
              value={getValueFromCartDetails(discountAmount)}
              onChange={handleValueChange}
              name={orderFormDetails.discount.name}
              error={!!errors?.[orderFormDetails.discount.name]}
              helperText={errors?.discounts}
              isAllowed={isAllowedDiscount}
              data-testid="discount-amount"
            />
          </BarDetails>
          <CartTotal
            errorMessage={
              maxAmount < totalAmount
                ? cartTotalErrorMessage.amountExceeding
                : totalAmount < 0
                ? cartTotalErrorMessage.discount
                : singleLenderCartAmountError
            }
            allowNegative={true}
            showError={
              maxAmount < totalAmount ||
              totalAmount < 0 ||
              !!singleLenderCartAmountError
            }
            quantity={cartTotalQuantity}
            totalAmount={totalAmount}
          />
        </OrderAdditionalDetailsContainer>
      )}
    </CartContainer>
  );
};
